import { useState, useEffect, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import * as priceOverrideService from '@/services/priceOverrideService'
import type { PriceOverride } from '@/types/priceOverride'
import { formatPrice } from '@/utils/helpers'

interface DefaultPrices {
  hourlyRate: number
  dailyRate: number
  overnightRate: number
  extraHourRate: number
  combo3hRate: number
  combo6h1hRate: number
  combo6h1hDiscount: number
}

interface PriceOverrideModalProps {
  open: boolean
  onClose: () => void
  dates: string[] // selected dates (1 = single edit, >1 = bulk)
  existingOverrides: PriceOverride[]
  defaultPrices: DefaultPrices
  target: { roomId?: string; roomType?: string }
  onSave: () => void
}

interface FieldDef {
  key: keyof DefaultPrices
  label: string
}

const PRICE_FIELDS: FieldDef[] = [
  { key: 'hourlyRate', label: 'Giá theo giờ' },
  { key: 'dailyRate', label: 'Giá theo ngày' },
  { key: 'overnightRate', label: 'Giá qua đêm' },
  { key: 'extraHourRate', label: 'Giá giờ phụ trội' },
  { key: 'combo3hRate', label: 'Giá combo 3 giờ' },
  { key: 'combo6h1hRate', label: 'Giá combo 6H+1H' },
  { key: 'combo6h1hDiscount', label: 'Giảm giá 6H+1H' },
]

function todayStr(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth() + 1
  const d = now.getDate()
  return `${y}-${m < 10 ? '0' : ''}${m}-${d < 10 ? '0' : ''}${d}`
}

export function PriceOverrideModal({
  open,
  onClose,
  dates,
  existingOverrides,
  defaultPrices,
  target,
  onSave,
}: PriceOverrideModalProps) {
  const isBulk = dates.length > 1
  const isSingle = dates.length === 1
  const today = todayStr()
  const hasPastDate = dates.some((d) => d < today)
  const conflictDates = isBulk
    ? existingOverrides.filter((o) => dates.includes(o.date)).map((o) => o.date)
    : []

  // Form state — null means "use default"
  const [values, setValues] = useState<Record<string, string>>({})
  const [applyDiscountSlots, setApplyDiscountSlots] = useState(false)
  const [saving, setSaving] = useState(false)

  // Reset form when modal opens or override changes
  useEffect(() => {
    if (!open) return
    const init: Record<string, string> = {}
    for (const f of PRICE_FIELDS) {
      const existing = existingOverrides[0]?.[f.key]
      init[f.key] = existing != null ? String(existing) : ''
    }
    setValues(init)
    setApplyDiscountSlots(existingOverrides[0]?.applyDiscountSlots ?? false)
  }, [open, existingOverrides])

  const handleFieldChange = useCallback((key: string, raw: string) => {
    // Allow empty or numeric
    const cleaned = raw.replace(/[^0-9]/g, '')
    setValues((prev) => ({ ...prev, [key]: cleaned }))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      // Build payload — only include non-empty fields
      const payload: Record<string, unknown> = { applyDiscountSlots }
      if (target.roomId) payload.roomId = target.roomId
      if (target.roomType) payload.roomType = target.roomType

      for (const f of PRICE_FIELDS) {
        const v = values[f.key]
        payload[f.key] = v !== '' ? Number(v) : null
      }

      if (isBulk) {
        await priceOverrideService.bulkUpsert({
          ...payload,
          dates,
        } as Parameters<typeof priceOverrideService.bulkUpsert>[0])
        toast.success(`Đã cập nhật giá cho ${dates.length} ngày`)
      } else {
        await priceOverrideService.upsert({
          ...payload,
          date: dates[0],
        } as Parameters<typeof priceOverrideService.upsert>[0])
        toast.success('Đã cập nhật giá')
      }
      onSave()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Lỗi khi lưu giá')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (existingOverrides.length === 0) return
    setSaving(true)
    try {
      await priceOverrideService.remove(existingOverrides[0].id)
      toast.success('Đã xoá override giá')
      onSave()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Lỗi khi xoá')
    } finally {
      setSaving(false)
    }
  }

  const title = isSingle
    ? `Chỉnh giá ngày ${dates[0]}`
    : `Chỉnh giá ${dates.length} ngày`

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {hasPastDate && (
          <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm text-yellow-800">
            Cảnh báo: Bao gồm ngày đã qua
          </div>
        )}

        {conflictDates.length > 0 && (
          <div className="rounded-md bg-orange-50 border border-orange-200 px-3 py-2 text-sm text-orange-800">
            <p className="font-medium">⚠ {conflictDates.length} ngày đã có giá ghi đè sẽ bị thay thế</p>
            <p className="mt-1 text-xs text-orange-600">
              {conflictDates.length <= 5
                ? conflictDates.join(', ')
                : `${conflictDates.slice(0, 5).join(', ')} ...và ${conflictDates.length - 5} ngày khác`}
            </p>
          </div>
        )}

        <div className="space-y-3 mt-2">
          {PRICE_FIELDS.map((f) => (
            <div key={f.key} className="grid grid-cols-[1fr_140px] items-center gap-3">
              <Label htmlFor={f.key} className="text-sm">
                {f.label}
              </Label>
              <Input
                id={f.key}
                type="text"
                inputMode="numeric"
                placeholder={formatPrice(defaultPrices[f.key])}
                value={values[f.key] ?? ''}
                onChange={(e) => handleFieldChange(f.key, e.target.value)}
              />
            </div>
          ))}

          <div className="flex items-center justify-between pt-1">
            <Label htmlFor="discount-slots" className="text-sm">
              Áp dụng giờ vàng
            </Label>
            <Switch
              id="discount-slots"
              checked={applyDiscountSlots}
              onCheckedChange={setApplyDiscountSlots}
            />
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          {isSingle && existingOverrides.length === 1 ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={saving}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Xoá
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Huỷ
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Đang lưu...' : conflictDates.length > 0 ? 'Xác nhận ghi đè' : 'Lưu'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
